/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * F032 step 7b: copy a week's workouts to a later week, or to another program.
 * Each copy is a new workout; a partial failure says exactly how many made it.
 */
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
} from "@lemonadetraining/web-ui";
import format from "date-fns/format";
import { getUserProgramming } from "@lemonadetraining/web-shared";
import { isPtExcludedFromProgrammePickersEnabled } from "@lemonadetraining/contracts";
import { useGymZone, type GymZone } from "../../../hooks/useGymZone";
import { useBoxSettings } from "../../../hooks/useBoxSettings";
import { addGymDays, gymKeySpan, keyToLocalDate, shiftGymDays } from "../../../lib/gymClock";
import { gymTimeLabel } from "../../time/gymTimeLabel";
import { useFeatureFlags } from "../../../hooks/useFeatureFlags";
import { useUserAccess } from "../../../hooks/useUserAccess";
import type { PlanData, PlannedWorkout } from "../../../hooks/useProgramWorkouts";
import { copyOfWorkout } from "./usePlannerEditing";
import type { ProgramDoc } from "../programTypes";

export type CopySource =
  | { kind: "dates"; fromKey: string; gym: GymZone; workouts: PlannedWorkout[] }
  | { kind: "week"; week: number; weeks: number; workouts: PlannedWorkout[] };

export interface CopyWeekDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  program: ProgramDoc;
  source: CopySource;
  /** Whether the source week has loaded; nothing is offered until it has. */
  status: "loading" | "ready" | "error";
  create: (data: PlanData, targetProgrammingUID?: string, index?: number) => Promise<unknown>;
  onCopied: () => void;
}

const THIS_PROGRAM = "this";
const LATER_WEEKS = [1, 2, 3, 4, 5, 6, 7, 8];

export function CopyWeekDialog({
  open,
  onOpenChange,
  program,
  source,
  status,
  create,
  onCopied,
}: CopyWeekDialogProps) {
  const access = useUserAccess();
  const uid = useSelector((state: { user: { user?: { uid?: string } } }) => state.user.user?.uid);
  const { flags, isLoading: flagsLoading } = useFeatureFlags(open && source.kind === "dates");
  const [target, setTarget] = useState(THIS_PROGRAM);
  const [offset, setOffset] = useState(1);
  const [toWeek, setToWeek] = useState<number | null>(null);
  const [others, setOthers] = useState<ProgramDoc[] | null>(null);
  const [othersFailed, setOthersFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [copied, setCopied] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!open) return;
    setCopied(new Set());
    setTarget(THIS_PROGRAM);
    setOffset(1);
    setResult(null);
    if (source.kind === "week") {
      setToWeek(source.week < source.weeks ? source.week + 1 : null);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open || source.kind !== "dates" || flagsLoading || !uid) return undefined;
    let live = true;
    setOthersFailed(false);
    getUserProgramming(uid, true, access.claims, {
      ptExcludedServerSide: isPtExcludedFromProgrammePickersEnabled(flags),
    })
      .then((list: ProgramDoc[]) => {
        if (!live) return;
        setOthers(
          (list ?? [])
            .map((p) => ({ ...p, programmingUID: p.id }))
            .filter(
              (p: ProgramDoc) =>
                p.id !== program.programmingUID && p.programmingType?.type === "reoccurring"
            )
        );
      })
      .catch((error: unknown) => {
        console.error("Error loading programs to copy to:", error);
        if (live) setOthersFailed(true);
      });
    return () => {
      live = false;
    };
  }, [open, source.kind, flagsLoading, uid]); // eslint-disable-line react-hooks/exhaustive-deps

  const count = source.workouts.length;
  const sameProgram = target === THIS_PROGRAM;
  const targetDoc = sameProgram ? undefined : others?.find((p) => p.id === target);
  const targetName = sameProgram ? program.name : (targetDoc?.name ?? "the program");
  // Another programme's dates live on its own gym's calendar (this browser's when it has none).
  const targetGym = useGymZone(useBoxSettings(targetDoc?.boxID || null).zone);
  const sourceZone = source.kind === "dates" ? source.gym.zone : null;
  const targetZone = sameProgram ? sourceZone : targetGym.zone;

  // A retry after a partial failure copies only what didn't make it, to the same place.
  const copyAll = async () => {
    setBusy(true);
    setResult(null);
    const pending = source.workouts.filter((w) => !copied.has(w.id));
    const outcomes = await Promise.allSettled(
      pending.map(async (w) => {
        const data = copyOfWorkout(w.workoutData);
        if (source.kind === "dates") {
          if (!sourceZone || !targetZone) {
            throw new Error("CopyWeekDialog: a gym's time zone has not loaded");
          }
          data.date = w.workoutData.date
            ? shiftGymDays(w.workoutData.date, offset * 7, sourceZone, targetZone)
            : null;
        } else {
          data.week = toWeek ?? source.week;
        }
        return create(data, sameProgram ? program.programmingUID : target, w.index);
      })
    );
    const nowCopied = new Set(copied);
    outcomes.forEach((o, i) => o.status === "fulfilled" && nowCopied.add(pending[i].id));
    setCopied(nowCopied);
    setBusy(false);
    if (outcomes.some((o) => o.status === "fulfilled")) onCopied();
    const failed = count - nowCopied.size;
    if (failed === 0) {
      onOpenChange(false);
      return;
    }
    const denied = outcomes.some(
      (o) => o.status === "rejected" && /permission-denied/.test(String(o.reason?.code ?? ""))
    );
    setResult(
      denied
        ? "You can't add workouts to that program. Pick one you run."
        : nowCopied.size === 0
          ? "Nothing was copied. Check your connection and try again."
          : `Copied ${nowCopied.size} of ${count}. Press Copy again to try the other ${failed}.`
    );
  };

  const weekLabel = (n: number) => {
    if (source.kind !== "dates") return `Week ${n}`;
    const from = addGymDays(source.fromKey, n * 7);
    const to = addGymDays(from, 6);
    const prefix = n === 0 ? "The same dates" : n === 1 ? "Next week" : `${n} weeks later`;
    return `${prefix} (${format(keyToLocalDate(from), "d MMM")} to ${format(keyToLocalDate(to), "d MMM")})`;
  };

  const gymDatesNote = (() => {
    if (source.kind !== "dates" || !source.gym.zone) return "";
    const lastKey = addGymDays(source.fromKey, offset * 7 + 6);
    const span = gymKeySpan(source.fromKey, lastKey, source.gym.zone);
    return source.gym.differs([span.startMs, span.endMs - 1])
      ? ` Dates use ${gymTimeLabel(source.gym.city)}.`
      : "";
  })();

  const locked = copied.size > 0;
  const canCopy =
    status === "ready" &&
    count > 0 &&
    !busy &&
    (source.kind === "dates"
      ? sourceZone !== null && targetZone !== null && (sameProgram ? offset > 0 : true)
      : toWeek !== null && toWeek !== source.week);

  return (
    <Dialog open={open} onOpenChange={(next) => !busy && onOpenChange(next)}>
      <DialogContent data-testid="copy-week-dialog">
        <DialogHeader>
          <DialogTitle>
            {source.kind === "dates" ? "Copy this week" : `Copy week ${source.week}`}
          </DialogTitle>
          <DialogDescription>
            {status === "loading"
              ? "Loading this week's workouts…"
              : status === "error"
                ? "We couldn't load this week's workouts. Close this and try again."
                : count === 0
                  ? "There are no workouts in this week to copy yet."
                  : `${count} ${count === 1 ? "workout" : "workouts"} will be copied. The originals stay as they are.${gymDatesNote}`}
          </DialogDescription>
        </DialogHeader>

        {count > 0 && source.kind === "dates" && (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="copy-target">Copy to</Label>
              <Select
                value={target}
                onValueChange={(v) => {
                  setTarget(v);
                  setOffset(v === THIS_PROGRAM ? 1 : 0);
                }}
              >
                <SelectTrigger id="copy-target" data-testid="copy-target" disabled={locked}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={THIS_PROGRAM}>This program</SelectItem>
                  {(others ?? []).map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {others === null && !othersFailed && (
                <p className="text-xs text-muted-foreground">Loading your other daily programs…</p>
              )}
              {othersFailed && (
                <p className="text-xs text-muted-foreground">
                  We couldn&apos;t load your other programs. You can still copy within this one.
                </p>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="copy-when">Which week</Label>
              <Select value={String(offset)} onValueChange={(v) => setOffset(Number(v))}>
                <SelectTrigger id="copy-when" data-testid="copy-when" disabled={locked}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(sameProgram ? LATER_WEEKS : [0, ...LATER_WEEKS]).map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {weekLabel(n)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {count > 0 && source.kind === "week" && (
          <div className="flex flex-col gap-2">
            <Label htmlFor="copy-to-week">Copy to</Label>
            <Select
              value={toWeek ? String(toWeek) : undefined}
              onValueChange={(v) => setToWeek(Number(v))}
            >
              <SelectTrigger id="copy-to-week" data-testid="copy-to-week" disabled={locked}>
                <SelectValue placeholder="Choose a week" />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: source.weeks }, (_, i) => i + 1)
                  .filter((n) => n !== source.week)
                  .map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      Week {n}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              The copies are added to that week; anything already there stays.
            </p>
          </div>
        )}

        {result && (
          <p role="alert" className="text-sm text-destructive">
            {result}
          </p>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            {count === 0 ? "Close" : "Cancel"}
          </Button>
          {count > 0 && (
            <Button onClick={copyAll} disabled={!canCopy} data-testid="confirm-copy-week">
              {busy && <Spinner className="mr-2 size-4" />}
              Copy to {sameProgram ? "this program" : targetName}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
